import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchCheckoutSummaryApi } from "../Apiservices/Auth/cart/Addtocart_api";

// -------------------------------
// INITIAL STATE
// -------------------------------
const initialState = {
  checkOutRequest: {
    event_id: null,
    priest: true,
    tent: true,
    catering: true,
    venue: true,
    priest_pct: 100,
    tent_pct: 100,
    catering_pct: 100,
    venue_pct: 100,
    coupon_code: "",
  },
  checkOutPageResponse: null,
  loading: false,
  refreshLoading: false,
  error: null,
};

// -------------------------------
// ASYNC ACTION (FIXED)
// -------------------------------
export const fetchCheckoutPage = createAsyncThunk(
  "checkout/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      // SAFE ACCESS (NO CRASH)
      const checkOutRequest = state.checkout?.checkOutRequest;

      if (!checkOutRequest) {
        console.error("❌ checkout state missing in store");
        return rejectWithValue("checkout state missing");
      }

      const res = await fetchCheckoutSummaryApi(checkOutRequest);
      console.log("Checkout API response:", res);

      if (res?.success) {
        return res.data;
      }

      return rejectWithValue(res?.message || "Failed to fetch checkout data");
    } catch (err) {
      console.error("fetchCheckoutPage error", err);
      return rejectWithValue(err?.response?.data?.message || err.message || "Error");
    }
  }
);

// -------------------------------
// SLICE
// -------------------------------
const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    // CLEAR REQUEST
    clearCheckoutRequest: (state, action) => {
      const event_id = action.payload;

      state.checkOutRequest = {
        event_id,
        priest: true,
        tent: true,
        catering: true,
        venue: true,
        priest_pct: 100,
        tent_pct: 100,
        catering_pct: 100,
        venue_pct: 100,
        coupon_code: "",
      };

      state.checkOutPageResponse = null;
      state.error = null;

      console.log("Checkout request cleared:", state.checkOutRequest);
    },

    // APPLY COUPON
    applyCoupon: (state, action) => {
      state.checkOutRequest.coupon_code = action.payload;
      state.error = null;
    },

    // REMOVE COUPON
    removeCoupon: (state) => {
      state.checkOutRequest.coupon_code = "";
      state.error = null;
    },

    // UPDATE PRIEST SELECTION
    updatePriestSelection: (state, action) => {
      const { percentage } = action.payload;
      state.checkOutRequest.priest = percentage != null && percentage > 0;
      state.checkOutRequest.priest_pct = percentage ?? 100;
      state.error = null;
    },

    // UPDATE TENT SELECTION
    updateTentSelection: (state, action) => {
      const { percentage } = action.payload;
      state.checkOutRequest.tent = percentage != null && percentage > 0;
      state.checkOutRequest.tent_pct = percentage ?? 100;
      state.error = null;
    },

    // UPDATE CATERING SELECTION
    updateCateringSelection: (state, action) => {
      const { percentage } = action.payload;
      state.checkOutRequest.catering = percentage != null && percentage > 0;
      state.checkOutRequest.catering_pct = percentage ?? 100;
      state.error = null;
    },

    // UPDATE VENUE SELECTION
    updateVenueSelection: (state, action) => {
      const { percentage } = action.payload;
      state.checkOutRequest.venue = percentage != null && percentage > 0;
      state.checkOutRequest.venue_pct = percentage ?? 100;
      state.error = null;
    },

    // UPDATE ALL SELECTIONS
    updateAllSelections: (state, action) => {
      const { priest, tent, catering, venue } = action.payload;
      
      if (priest !== undefined) {
        state.checkOutRequest.priest = priest;
        state.checkOutRequest.priest_pct = priest ? 100 : 0;
      }
      if (tent !== undefined) {
        state.checkOutRequest.tent = tent;
        state.checkOutRequest.tent_pct = tent ? 100 : 0;
      }
      if (catering !== undefined) {
        state.checkOutRequest.catering = catering;
        state.checkOutRequest.catering_pct = catering ? 100 : 0;
      }
      if (venue !== undefined) {
        state.checkOutRequest.venue = venue;
        state.checkOutRequest.venue_pct = venue ? 100 : 0;
      }
      state.error = null;
    },

    // SET CHECKOUT REQUEST
    setCheckoutRequest: (state, action) => {
      state.checkOutRequest = {
        ...state.checkOutRequest,
        ...action.payload,
      };
      state.error = null;
    },

    // CLEAR ERROR
    clearError: (state) => {
      state.error = null;
    },

    // RESET CHECKOUT
    resetCheckout: (state) => {
      state.checkOutPageResponse = null;
      state.loading = false;
      state.refreshLoading = false;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchCheckoutPage.pending, (state) => {
        if (!state.checkOutPageResponse) {
          state.loading = true;
        } else {
          state.refreshLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchCheckoutPage.fulfilled, (state, action) => {
        state.checkOutPageResponse = action.payload;
        state.loading = false;
        state.refreshLoading = false;
        state.error = null;
        console.log("Checkout page fetched successfully");
      })
      .addCase(fetchCheckoutPage.rejected, (state, action) => {
        console.error("Checkout fetch failed:", action.payload);
        state.checkOutPageResponse = null;
        state.loading = false;
        state.refreshLoading = false;
        state.error = action.payload || "Failed to fetch checkout data";
      });
  },
});

// -------------------------------
// SELECTORS
// -------------------------------

// Get checkout request
export const selectCheckoutRequest = (state) => state.checkout?.checkOutRequest;

// Get checkout response
export const selectCheckoutResponse = (state) => state.checkout?.checkOutPageResponse;

// Get loading state
export const selectCheckoutLoading = (state) => state.checkout?.loading || false;

// Get refresh loading state
export const selectCheckoutRefreshLoading = (state) => state.checkout?.refreshLoading || false;

// Get error state
export const selectCheckoutError = (state) => state.checkout?.error || null;

// Get venue items from checkout response
export const selectVenueItems = (state) => {
  const response = state.checkout?.checkOutPageResponse;
  return response?.venue_items || [];
};

// Get all items grouped by type
export const selectCheckoutItems = (state) => {
  const response = state.checkout?.checkOutPageResponse;
  if (!response) return null;

  return {
    priest: response.priestItems || [],
    tent: response.tent_providers || [],
    catering: response.catering_providers || [],
    venue: response.venue_items || [],
  };
};

// Get payment summary
export const selectPaymentSummary = (state) => {
  const response = state.checkout?.checkOutPageResponse;
  if (!response) return null;

  return {
    total: response.total || 0,
    paid: response.paid || 0,
    balanceToPay: response.balanceToPay || 0,
    totalPayableNow: response.totalPayableNow || 0,
    payLaterBalance: response.payLaterBalance || 0,
    coupon: response.coupon || null,
    newCouponDiscount: response.newCouponDiscount || 0,
    logs: response.logs || [],
    serviceBreakdown: response.service_breakdown || {},
  };
};

// Get service breakdown
export const selectServiceBreakdown = (state) => {
  const response = state.checkout?.checkOutPageResponse;
  return response?.service_breakdown || {};
};

// Get venue payment summary
export const selectVenuePaymentSummary = (state) => {
  const response = state.checkout?.checkOutPageResponse;
  return response?.service_breakdown?.venue || {};
};

// Check if venue is selected
export const selectIsVenueSelected = (state) => {
  const request = state.checkout?.checkOutRequest;
  return request?.venue !== false;
};

// -------------------------------
// EXPORT ACTIONS
// -------------------------------
export const {
  clearCheckoutRequest,
  applyCoupon,
  removeCoupon,
  updatePriestSelection,
  updateTentSelection,
  updateCateringSelection,
  updateVenueSelection,
  updateAllSelections,
  setCheckoutRequest,
  clearError,
  resetCheckout,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;